import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "react-hot-toast";
import { houseService } from "@/services/houseService";
import { hostService } from "@/services/hostService";
import type { HouseCreate } from "@/types";
import { Label, Select, Alert, type SelectOption } from "@/components/ui";
import { PageFrame } from "@/components/common";
import { CreateHouseModal } from "@/pages/houses/components/CreateHouseModal";
import { getApiErrorMessage, toastApiError } from "@/lib/errorMessages";
import { useGoBack } from "@/hooks/useGoBack";

export default function AdminCreateHousePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("houses");
  const [selectedHostId, setSelectedHostId] = useState("");
  const [modalOpen, setModalOpen] = useState(true);

  const fixedHostId = searchParams.get("host_user_id") ?? undefined;
  const inferHost = Boolean(fixedHostId);

  const {
    data: hosts = [],
    isLoading: hostsLoading,
    error: hostsError,
    refetch: refetchHosts,
  } = useQuery({
    queryKey: ["hosts", "admin-create-house"],
    queryFn: () => hostService.getAll(),
    enabled: !inferHost,
  });

  const hostOptions: SelectOption[] = useMemo(
    () =>
      hosts.map((h) => ({
        value: h.user_id,
        label: t("list.hostOptionLabel", { name: h.name }),
      })),
    [hosts, t],
  );

  const effectiveHostId = useMemo(() => {
    if (fixedHostId) return fixedHostId;
    if (hosts.length === 0) return "";
    if (selectedHostId && hosts.some((h) => h.user_id === selectedHostId)) {
      return selectedHostId;
    }
    return hosts[0].user_id;
  }, [fixedHostId, hosts, selectedHostId]);

  const backFallback =
    inferHost && fixedHostId ? `/hosts/${fixedHostId}` : "/houses";
  const goBack = useGoBack(backFallback);
  const backLabel = inferHost ? t("adminCreate.backToHost") : t("adminCreate.back");

  const createMutation = useMutation({
    mutationFn: (data: Omit<HouseCreate, "host_user_id">) =>
      houseService.create({ ...data, host_user_id: effectiveHostId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["houses", effectiveHostId] });
      queryClient.invalidateQueries({
        queryKey: ["host-houses", effectiveHostId],
      });
      queryClient.invalidateQueries({ queryKey: ["host", effectiveHostId] });
      toast.success(t("createSuccess"));
      setModalOpen(false);
      if (inferHost && fixedHostId) {
        navigate(`/hosts/${fixedHostId}`);
      } else {
        navigate("/houses");
      }
    },
    onError: (err) => {
      toastApiError(err, "createHouse");
    },
  });

  if (!inferHost && hostsLoading) {
    return (
      <PageFrame
        backLink={{
          variant: "ghost",
          fallback: backFallback,
          children: t("adminCreate.back"),
        }}
        isLoading
      />
    );
  }

  if (!inferHost && hostsError) {
    return (
      <PageFrame
        backLink={{
          variant: "ghost",
          fallback: backFallback,
          children: t("adminCreate.back"),
        }}
        error
        errorMessage={getApiErrorMessage(hostsError, "loadHosts")}
        onRetry={() => refetchHosts()}
      />
    );
  }

  if (!inferHost && hosts.length === 0) {
    return (
      <PageFrame
        backLink={{
          variant: "ghost",
          fallback: backFallback,
          children: t("adminCreate.back"),
        }}
      >
        <Alert variant="warning">{t("list.emptyHosts")}</Alert>
      </PageFrame>
    );
  }

  if (!effectiveHostId) {
    return (
      <PageFrame
        backLink={{
          variant: "ghost",
          fallback: backFallback,
          children: t("adminCreate.back"),
        }}
      />
    );
  }

  return (
    <PageFrame
      backLink={{
        variant: "ghost",
        fallback: backFallback,
        children: backLabel,
      }}
      actions={
        !inferHost ? (
          <div className="max-w-md flex-1 space-y-2">
            <Label>{t("list.hostLabel")}</Label>
            <Select
              aria-label={t("list.selectHostAria")}
              options={hostOptions}
              value={effectiveHostId}
              onChange={(e) => setSelectedHostId(e.target.value)}
            />
          </div>
        ) : undefined
      }
    >
      <CreateHouseModal
        isOpen={modalOpen}
        onClose={goBack}
        onSubmit={(data) => createMutation.mutate(data)}
        isLoading={createMutation.isPending}
      />
    </PageFrame>
  );
}
